// Animation system for battle visual effects

import { Canvas } from '../Canvas';
import { CONSTANTS } from '../types';

export interface DamageNumber {
  id: string;
  gladiatorId: string;
  value: number;
  isMiss: boolean;
  x: number;
  y: number;
  alpha: number;
  lifetime: number;
  maxLifetime: number;
  offsetY: number;
}

export enum HitMarkerType {
  HIT = 0,
  MISS = 1,
  KO = 2,
  SPELL_1 = 3,
  SPELL_2 = 4,
}

export interface HitMarker {
  id: string;
  type: HitMarkerType;
  x: number;
  y: number;
  alpha: number;
  lifetime: number;
  maxLifetime: number;
  scale: number;
}

export class AnimationSystem {
  private damageNumbers: DamageNumber[] = [];
  private hitMarkers: HitMarker[] = [];
  private hitMarkerAssets: HTMLCanvasElement[] = [];
  private nextId = 0;

  private static readonly DAMAGE_NUMBER_LIFETIME = 1.5; // seconds
  private static readonly HIT_MARKER_LIFETIME = 0.8; // seconds
  private static readonly FLOAT_DISTANCE = 40; // pixels

  setHitMarkerAssets(assets: HTMLCanvasElement[]): void {
    this.hitMarkerAssets = assets;
  }

  addDamageNumber(
    gladiatorId: string,
    value: number,
    tileX: number,
    tileY: number,
    isMiss: boolean = false
  ): void {
    // Convert tile coordinates to screen coordinates
    const startX = 50; // Map offset
    const startY = 60;
    const tileSize = CONSTANTS.MAP_TILE_SIZE;

    const x = startX + tileX * tileSize + tileSize / 2;
    const y = startY + tileY * tileSize;

    this.damageNumbers.push({
      id: `dmg-${this.nextId++}`,
      gladiatorId,
      value,
      isMiss,
      x,
      y,
      alpha: 1.0,
      lifetime: AnimationSystem.DAMAGE_NUMBER_LIFETIME,
      maxLifetime: AnimationSystem.DAMAGE_NUMBER_LIFETIME,
      offsetY: 0,
    });
  }

  addHitMarker(
    type: HitMarkerType,
    tileX: number,
    tileY: number
  ): void {
    const startX = 50;
    const startY = 60;
    const tileSize = CONSTANTS.MAP_TILE_SIZE;

    const x = startX + tileX * tileSize + tileSize / 2;
    const y = startY + tileY * tileSize + tileSize / 2;

    this.hitMarkers.push({
      id: `hit-${this.nextId++}`,
      type,
      x,
      y,
      alpha: 1.0,
      lifetime: AnimationSystem.HIT_MARKER_LIFETIME,
      maxLifetime: AnimationSystem.HIT_MARKER_LIFETIME,
      scale: 1.0,
    });
  }

  update(deltaTime: number): void {
    // Update damage numbers
    for (let i = this.damageNumbers.length - 1; i >= 0; i--) {
      const dmg = this.damageNumbers[i];
      dmg.lifetime -= deltaTime;

      if (dmg.lifetime <= 0) {
        this.damageNumbers.splice(i, 1);
        continue;
      }

      // Calculate animation progress (0 to 1)
      const progress = 1 - (dmg.lifetime / dmg.maxLifetime);

      // Float upward
      dmg.offsetY = -AnimationSystem.FLOAT_DISTANCE * progress;

      // Fade out in the last 30% of lifetime
      if (progress > 0.7) {
        dmg.alpha = 1 - ((progress - 0.7) / 0.3);
      }
    }

    // Update hit markers
    for (let i = this.hitMarkers.length - 1; i >= 0; i--) {
      const marker = this.hitMarkers[i];
      marker.lifetime -= deltaTime;

      if (marker.lifetime <= 0) {
        this.hitMarkers.splice(i, 1);
        continue;
      }

      const progress = 1 - (marker.lifetime / marker.maxLifetime);

      // Scale up slightly at start, then fade
      if (progress < 0.2) {
        marker.scale = 1 + (progress / 0.2) * 0.3; // Scale from 1.0 to 1.3
      } else {
        marker.scale = 1.3 - ((progress - 0.2) / 0.8) * 0.3; // Scale back to 1.0
      }

      // Fade out in the last 40% of lifetime
      if (progress > 0.6) {
        marker.alpha = 1 - ((progress - 0.6) / 0.4);
      }
    }
  }

  render(canvas: Canvas): void {
    // Render hit markers first (behind damage numbers)
    for (const marker of this.hitMarkers) {
      this.renderHitMarker(canvas, marker);
    }

    // Render damage numbers
    for (const dmg of this.damageNumbers) {
      this.renderDamageNumber(canvas, dmg);
    }
  }

  private renderDamageNumber(canvas: Canvas, dmg: DamageNumber): void {
    const text = dmg.isMiss ? 'MISS' : `-${dmg.value}`;
    const color = dmg.isMiss ? '#888888' : '#ff0000';

    const ctx = canvas.getContext();
    ctx.save();
    ctx.globalAlpha = dmg.alpha;

    // Draw with outline for better visibility
    canvas.drawOutlinedText(
      text,
      dmg.x,
      dmg.y + dmg.offsetY - 15,
      'bold 14px "comic sans ms"',
      color,
      '#000000',
      2,
      'center'
    );

    ctx.restore();
  }

  private renderHitMarker(canvas: Canvas, marker: HitMarker): void {
    const asset = this.hitMarkerAssets[marker.type];
    if (!asset) return;

    const ctx = canvas.getContext();
    ctx.save();
    ctx.globalAlpha = marker.alpha;

    const width = asset.width * marker.scale;
    const height = asset.height * marker.scale;
    const x = marker.x - width / 2;
    const y = marker.y - height / 2;

    ctx.drawImage(asset, x, y, width, height);
    ctx.restore();
  }

  clearAll(): void {
    this.damageNumbers = [];
    this.hitMarkers = [];
  }

  hasPendingAnimations(): boolean {
    return this.damageNumbers.length > 0 || this.hitMarkers.length > 0;
  }
}
